// BeatVisualController — subscribes ONCE to the BeatClock and dispatches beat events to
// registered visual listeners, gated by all activation conditions. Decorative only: it never
// delays input and never affects gameplay/scoring. Includes a performance guard, one-time
// flourish dedup (shared key family with result/ceremony audio), and founder diagnostics.
import beatClock from "./BeatClock";
import visualBeatRegistry from "./visualBeatRegistry";
import { ONE_TIME_EFFECTS } from "./beatVisualEvents";
import { track } from "../api";

const FRAME_BUDGET_MS = 34;   // ~2 dropped frames → suppress decorative effects
const PERF_WINDOW = 20;

class BeatVisualController {
  constructor() {
    if (BeatVisualController._i) return BeatVisualController._i;
    this.active = false;
    this._gate = false;
    this.reducedMotion = false;
    this._unsub = null;
    this.playedFlourishes = new Set(); // dedup one-time visual flourishes
    this.diag = {
      lastEvent: null, lastDedupKey: null, triggered: 0, suppressed: 0,
      fallbackUsed: 0, errors: 0, queued: 0, activeEffects: [], perfSuppressed: false,
    };
    this._frameTimes = [];
    this._onDiag = null;
    BeatVisualController._i = this;
  }

  onDiag(cb) { this._onDiag = cb; }
  _emit() { if (this._onDiag) try { this._onDiag(this.snapshot()); } catch (e) { /* noop */ } }

  _effective() { return this._gate === true && this.reducedMotion !== true; }

  // Reconcile the clock subscription with the effective state (gate AND not reduced-motion).
  _reconcile() {
    const eff = this._effective();
    this.active = eff;
    if (eff && !this._unsub) {
      this._unsub = beatClock.subscribe((ev) => this._dispatch(ev));
      track("beat_clock_started");
    } else if (!eff && this._unsub) {
      this._unsub(); this._unsub = null;
    }
    this._emit();
  }

  setReducedMotion(v) {
    this.reducedMotion = !!v;
    if (this.reducedMotion) this.diag.suppressed += 1;
    this._reconcile();
  }

  // Called by the provider with the fully-resolved activation gate.
  setActive(active) {
    this._gate = !!active;
    this._reconcile();
  }

  _perfOk() {
    const now = performance.now();
    if (this._lastFrame) {
      const dt = now - this._lastFrame;
      this._frameTimes.push(dt);
      if (this._frameTimes.length > PERF_WINDOW) this._frameTimes.shift();
      const avg = this._frameTimes.reduce((a, b) => a + b, 0) / this._frameTimes.length;
      this.diag.perfSuppressed = avg > FRAME_BUDGET_MS;
    }
    this._lastFrame = now;
    return !this.diag.perfSuppressed;
  }

  _dispatch(ev) {
    this.diag.lastEvent = { type: ev.type, at: ev.at };
    if (!this.active || this.reducedMotion) { this.diag.suppressed += 1; this._emit(); return; }
    if (!this._perfOk()) { this.diag.suppressed += 1; track("beat_visual_suppressed"); this._emit(); return; }
    const listeners = visualBeatRegistry.forEvent(ev.type);
    let primary = 0, supporting = 0;
    listeners.forEach((l) => {
      const prominent = l.intensity === "strong" || l.intensity === "moderate";
      if (prominent && primary >= 1) { supporting += 1; }
      if (prominent) primary += 1; else supporting += 1;
      if (supporting > 2 && !prominent) { this.diag.suppressed += 1; return; }
      try { l.cb && l.cb(ev); this.diag.triggered += 1; }
      catch (e) { this.diag.errors += 1; track("beat_visual_error"); }
    });
    this.diag.activeEffects = visualBeatRegistry.effects();
    this._emit();
  }

  // One-time flourish gate (result/ceremony/card). Returns true if it should render the flourish.
  claimFlourish(key) {
    if (!key) return true;
    this.diag.lastDedupKey = key;
    if (this.playedFlourishes.has(key)) return false;
    this.playedFlourishes.add(key);
    return true;
  }

  fallback(reason) { this.diag.fallbackUsed += 1; track("beat_visual_fallback_used"); this._emit(); return reason; }

  // Founder test controls (diagnostics only; do not touch gameplay records).
  triggerManual(type) { beatClock.emitManual ? beatClock.emitManual(type) : null; this._dispatch({ type, manual: true, at: Date.now() }); }
  cancelQueued() { this.diag.queued = 0; this._emit(); }
  clearLog() { this.diag = { ...this.diag, triggered: 0, suppressed: 0, fallbackUsed: 0, errors: 0, lastEvent: null, lastDedupKey: null }; this.playedFlourishes = new Set(); this._emit(); }

  snapshot() {
    return {
      active: this.active, reducedMotion: this.reducedMotion,
      listeners: visualBeatRegistry.count(), effects: visualBeatRegistry.effects(),
      clock: beatClock.status(), ...this.diag,
    };
  }
}

export const beatVisualController = new BeatVisualController();
export default beatVisualController;
